import { PEER } from '../../constants'

/**
 * Event wrapper for `icecandidate` event.
 * Reference: developer.mozilla.org/en-US/docs/Web/API/RTCPeerConnection/onicecandidate
 * @method onicecandidate
 * @param  {Function} listener The listener function for the event.
 * @return {Boolean}  Whether the assignment succeeded or not.
 */
export default function onicecandidate (listener) {
  const { nativePeer, proxyPeer, config, iceTimer, emitter, iceCandidates, log } = this

  /**
   * Intercept the PeerConnection onicecandidate event.
   * Handle the candidate as defined by the current trickle ICE mode config.
   * Trickle ICE scenarios:
   *   - FULL: Trickle.
   *   - HALF, pre-half: Wait for "half" or null candidate.
   *   - HALF, post-half: Trickle.
   *   - NONE: Wait for null candidate.
   */
  nativePeer.onicecandidate = event => {
    log.debug(
      `ICE candidate received (trickling?: ${config.trickleIceMode === PEER.TRICKLE_ICE.FULL}): `,
      event.candidate
    )

    // Keep track of all candidates gathered by this collection process.
    if (event.candidate !== null) {
      iceCandidates.push(event.candidate)
    }

    // Emit an event for the session to pick up for the ice collection check
    const elapsedTime = iceTimer.timeFromStart()
    emitter.emit('iceCandidateCollected', {
      iceCollectionDuration: elapsedTime,
      iceCandidates,
      iceGatheringState: proxyPeer.iceGatheringState,
      rtcPeerConnectionConfig: config,
      rtcLocalSessionDescription: proxyPeer.localDescription
    })

    if (config.trickleIceMode === PEER.TRICKLE_ICE.FULL) {
      // If trickling is enabled, emit an event for every ICE candidate. The
      //    Peer is already ready for negotiation at this point.
      if (event.candidate) {
        // Only trickle non-null (ie. actual) candidates.
        listener(event)
      }
    } else if (config.trickleIceMode === PEER.TRICKLE_ICE.HALF) {
      // For half trickle, only start trickling after a certain threshold.
      //    Peer will be considered ready for negotiation after that point.
      const haveHalf = config.halfTrickleThreshold({
        sdp: proxyPeer.localDescription.sdp,
        iceCandidate: event.candidate,
        time: elapsedTime
      })

      if (haveHalf) {
        log.debug('Half ICE collection process complete; ready for negotiation.')
        config.trickleIceMode = PEER.TRICKLE_ICE.FULL
        emitter.emit('onnegotiationready')
      } else {
        log.debug("Peer's half trickle threshold not reached.")
      }
    }
  }

  return true
}
